<template>
    <div id="loginformsection">
        <form v-on:submit="validateForm" class="login-form">
            <loader-overlay :isLoading="isLoggingIn || isPasswordResetting"></loader-overlay>
            <div class="form-row" v-if="!hasUsernameLabel">
                <div class="mb-3 col-12">
                    <slot name="username"></slot>
                    <input
                        type="email"
                        class="form-control usrinput lo-sensitive"
                        id="username"
                        v-model="displayUsername"
                        :placeholder="showPlaceholders ? 'Username' : ''"
                        autocomplete="username"
                        v-bind:disabled="disableInput"
                        :aria-invalid="usernameError ? 'true' : 'false'"
                        :aria-describedby="enableInlineValidation && usernameError ? 'usernameError' : ''"
                        @blur="doUsernameValidation"
                        @input="clearUsernameError"
                        :class="{ 'is-invalid': enableInlineValidation && usernameError }"
                    />
                    <i
                        class="fa fa-exclamation-circle error fainputerror"
                        v-if="enableInlineValidation && usernameError"
                    ></i>
                    <p id="usernameError" v-if="enableInlineValidation && usernameError" class="text-danger mb-0">
                        {{ usernameError }}
                    </p>
                </div>
            </div>

            <div class="mb-3" v-else>

                <div class="col-12 ps-0 col-lg-6">
                    <label v-if="passwordReset" for="username"
                        class="fw-bold col-form-label col-form-label-sm text-end rplbl">{{ usernameLabel }}
                        <span class="required">*</span></label>


                    <input type="email" class="form-control usrinput lo-sensitive" id="username"
                        v-model="displayUsername" autocomplete="username" placeholder="Email Address"
                        v-bind:disabled="disableInput">
                </div>

            </div>

            <div class="form-row" v-if="!hasUsernameLabel">
                <div class="mb-3 col-12">
                    <slot name="password"></slot>
                    <input
                        :type="passwordFieldType"
                        class="form-control usrinput lo-sensitive"
                        id="password"
                        v-model="password"
                        :placeholder="showPlaceholders ? 'Password' : ''"
                        autocomplete="current-password"
                        v-bind:disabled="disableInput"
                        :aria-invalid="passwordError ? 'true' : 'false'"
                        :aria-describedby="enableInlineValidation && passwordError ? 'passwordError' : ''"
                        @blur="doPasswordValidation"
                        @input="clearPasswordError"
                        :class="{ 'is-invalid': enableInlineValidation && passwordError }"
                    />
                    <i
                        class="fa fa-exclamation-circle error fainputerror shift"
                        v-if="enableInlineValidation && passwordError && showPasswordToggle"
                    ></i>
                    <template v-if="showPasswordToggle">
                        <i
                            :class="passwordVisible ? 'fa fa-eye-slash fainput' : 'fa fa-eye fainput'"
                            @click="togglePasswordVisibility"
                            title="Toggle Password Visibility"
                        ></i>
                    </template>
                    <p id="passwordError" v-if="enableInlineValidation && passwordError" class="text-danger mb-0">
                        {{ passwordError }}
                    </p>
                </div>

                <!--Inline invalid credentials error message block -->
                <div class="mb-3 col-12 d-flex align-items-center" v-if="enableInlineValidation && inlineErrorMessage">
                    <i class="fa fa-exclamation-circle text-danger me-2"></i>
                    <p class="text-danger mb-0">{{ inlineErrorMessage }}</p>
                </div>
                
            </div>
            <div class="mb-3" v-else>
                <div class="col-12 ps-0 col-lg-6">
                    <label v-if="passwordReset" for="username"
                        class="fw-bold col-form-label col-form-label-sm text-end rplbl">Date of Birth: <span
                            class="required">*</span></label>

                    <input type="text" class="form-control form-control-sm usrinput lo-sensitive" id="username"
                        v-model="dateOfBirth" placeholder="(mm/dd/yyyy)" v-mask="'##/##/####'"
                        v-bind:disabled="disableInput">
                    <font-awesome-icon :icon="['far', 'question-circle']" class="fainput " fixed-width
                    v-tooltip="{
                            content: 'Your date of birth is required to reset your password. It will also help our support team authenticate you if you need assistance.',
                            placement: 'left'
                    }" />
                </div>
            </div>

            <div class="form-row" v-if="!passwordReset && rememberMe">
                <div class="form-check text-start pb-3 custom-ps">
                    <input type="checkbox" class="form-check-input lo-sensitive" id="chkrememberMe"
                        v-model="shouldRememberMe" v-bind:disabled="disableInput" />
                    <label class="form-check-label" for="chkrememberMe">
                        Remember my Username (Email)
                    </label>
                </div>
            </div>

            <div class="form-row">
                <div class="mb-3 col-12" v-if="!passwordReset">
                    <input type="submit" class="btn btn-primary w-100 login-btn"
                        :class="{ 'inverted': invertLoginButtonColors }" :value="loginLabel"
                        v-bind:disabled="disableInput" />
                </div>
                <div class="mb-3 col-12" v-else>
                    <div class="col-12 pt-4 ps-0 pb-5 col-lg-6">
                        <!--<div class="col-6 col-sm-8 offset-sm-4">-->
                        <input type="submit" class="btn btn-primary w-100 login-btn"
                            :class="{ 'inverted': invertLoginButtonColors }" value="Reset Password"
                            v-bind:disabled="isPasswordResetting || disableInput" />

                    </div>
                </div>
            </div>

            <form-result-message v-if="!enableInlineValidation" :message="errorMessage" :error="true"></form-result-message>
        </form>
        <div class="help-container" v-if="!passwordReset" v-bind:class="{ 'text-start': alignHelpLinksLeft }"
            id="help-container">
            <span class="help-links">
                <nav-link href="/loginhelp">Forgot ID or Password</nav-link>
                <span class="help-link-seperator" v-if="displayRegisterNow">|</span>
                <nav-link href="/registernow" v-if="displayRegisterNow" sourcePage="Login Form">Register Now</nav-link>
            </span>
            &nbsp;&nbsp;

            <div id="loginFormDiv" class="d-none d-md-inline-block" v-if="!hideInfoTooltip"
            v-tooltip="{
                    content: 'Your username is the email address you used to activate your account. Your password must be between 8 to 32 characters in length and include at least one letter and one number.',
                    placement: 'left'
            }" >
                <font-awesome-layers class="fa-fw icn d-md-inline-block" style="vertical-align:middle;" fixed-width
                    id="infoIcon">
                    <font-awesome-icon :icon="['far', 'circle']" class="facolor" />
                    <font-awesome-layers-text class="faItalic ie nonie" value="i"></font-awesome-layers-text>
                </font-awesome-layers>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { vMaska } from "maska/vue";
import moment from 'moment';

import axios from 'axios';
import siteApi from '../../api/siteApi';

import FormResultMessage from './FormResultMessage.vue';

import utilitiesMixin from '../../mixins/Utilities';

export default {
    name: 'login-form',
    analyticsCategory: 'Login Form',
    directives: { mask: vMaska },
    components: {
        'form-result-message': FormResultMessage
    },
    mixins: [utilitiesMixin],
    data: function () {
        return {
            password: '',
            dateOfBirth: '',
            shouldRememberMe: false,
            errorMessage: '',
            isLoggingIn: false,
            disableInput: false,
            isPasswordResetting: false,
            displayUsername: this.username,
            passwordVisible: false,
            usernameError: '',
            passwordError: '',
            inlineErrorMessage: '',
        }
    },
    props: {
        username: { type: String, default: '' },
        rememberMe: { type: Boolean, default: false },
        passwordReset: { type: Boolean, default: false },
        usernameLabel: { type: String, default: '' },
        loginLabel: { type: String, default: 'Log In' },
        displayRegisterNow: { type: Boolean, default: true },
        redirectUri: { type: String, default: '' },
        allowInactiveAccounts: { type: Boolean, default: false },
        alignHelpLinksLeft: { type: Boolean, default: false },
        invertLoginButtonColors: { type: Boolean, default: false },
        showPlaceholders: { type: Boolean, default: true },
        showPasswordToggle: { type: Boolean, default: false },
        enableInlineValidation: { type: Boolean, default: false },
        hideInfoTooltip: { type: Boolean, default: false },
    },
    mounted() {
        if (this.rememberMe && !this.passwordReset) {
            this.shouldRememberMe = this.checkRememberMeCookie();
        }
    },
    computed: {
        ...mapGetters(['returnUrl', 'defaultReturnUrl']),
        hasUsernameLabel() {
            return this.usernameLabel !== '';
        },
        passwordFieldType() {
            return this.passwordVisible ? 'text' : 'password';
        },
    },
    methods: {
        ...mapActions(['loginRedirect']),
        ...mapMutations(['setWebUser', 'toggleFooter']),
        checkRememberMeCookie() {
            var user = this.getCookie('pulteusername');
            if (user !== undefined && user !== null && user !== '') {
                this.displayUsername = user;
                return true;
            }
            return false;
        },
        clearLoginMessage() {
            this.errorMessage = '';
        },
        displayLoginMessage() {
            this.errorMessage = "Please log in";
        },
        doDateOfBirthValidation() {
            if (this.dateOfBirth === undefined || this.dateOfBirth === null || this.dateOfBirth === '') {
                this.errorMessage = 'Please enter your date of birth';
                return false;
            }

            var date = moment(this.dateOfBirth, "MM/DD/YYYY", true);
            if (!date.isValid()) {
                this.errorMessage = 'Please enter a valid date';
                return false;
            }

            return true;
        },
        doPasswordValidation() {

            if (this.password === undefined || this.password === null || this.password === '') {
                this.errorMessage = 'A password is required';
                if (this.enableInlineValidation) {
                    this.passwordError = 'Please enter your password';
                    this.errorMessage = '';
                    this.inlineErrorMessage = '';
                }
                return false;
            }
            return true;
        },
        doUsernameValidation() {
            if (this.displayUsername === undefined || this.displayUsername === null || this.displayUsername === '') {
                this.errorMessage = 'Please enter a username'
                if (this.enableInlineValidation) {
                    this.usernameError = 'Please enter a valid email';
                    this.errorMessage = '';
                    this.inlineErrorMessage = '';
                }
                return false;
            }
            else if (this.enableInlineValidation && !this.isValidEmail(this.displayUsername)) {
                    this.usernameError = 'Please enter a valid email';
                    return false;
            }
            return true;
        },
        clearUsernameError() {
            this.usernameError = '';
            this.inlineErrorMessage = '';
        },
        clearPasswordError() {
            this.passwordError = '';
            this.inlineErrorMessage = '';
        },
        isValidEmail(email) {
            const emailReg = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            return emailReg.test(email);
        },
        togglePasswordVisibility() {
            this.passwordVisible = !this.passwordVisible;
        },
        getCookie(cname) {
            var name = cname + "=";
            var decodedCookie = decodeURIComponent(document.cookie);
            var ca = decodedCookie.split(';');
            for (var i = 0; i < ca.length; i++) {
                var c = ca[i];
                while (c.charAt(0) == ' ') {
                    c = c.substring(1);
                }
                if (c.indexOf(name) == 0) {
                    return c.substring(name.length, c.length);
                }
            }
            return "";
        },
        login(event) {
            var self = this;

            self.isLoggingIn = true;
            self.disableInput = true;

            var data = {
                Username: this.displayUsername,
                Password: this.password,
                ReturnUrl: this.returnUrl ? this.returnUrl : this.defaultReturnUrl,
                RememberLogin: false
            };

            return axios.post('/api/identity/login/', data)
                .then(loginResponse => {
                    var localPassword = self.password;
                    self.password = '';

                    if (loginResponse && loginResponse.data && loginResponse.data.isAuthenticated === false) {
                        // authentication failed
                        if (loginResponse.data.isLockedOut === true) {
                            self.errorMessage = "This account has been locked.";

                            console.log('app: login: the account has been locked due to bad login attempts');
                            self.setWebUser({
                                isLockedout: true,
                                lockOutEnd: loginResponse.data.lockoutEndDateUtc
                            });
                            console.log('app: login: redirecting to lockedout....')

                            this.utilities.navigation.doRedirect({ name: 'lockedout' }, this.$route.name, this.$options.analyticsCategory);
                            return;

                        } else if (loginResponse.data.isRevoked === true) {
                            self.errorMessage = "This account is no longer active. Please contact support.";
                            return;

                        } 
                         
                        else if (loginResponse.data.isInactive === true) {
                            if(loginResponse.data.requiresTwoFactor === true){
                                return this.$store.dispatch('setTwoFAEmail', self.displayUsername).then(x=> {
                                    return  this.$store.dispatch('setNewUserpassword', localPassword).then(x=> {
                                       return  this.$store.dispatch('setIsUserInactive',true).then(x=> {
                                        return  this.$store.dispatch('setIsNewRegistration',false).then(x=> {

                                            var twofaUrl = `/twoStepVerification`;
                                                this.$router.push({ path: twofaUrl });
                                        });

                                      
                                       });
                                    });
                                });

                            }
                            else if (self.allowInactiveAccounts === false) {
                                self.errorMessage = "This account requires activation.";
                                return;
                            }
                        }
                        //Error specific to an account missing the webUserAccount and needing to be backfilled
                        else if (loginResponse.data.isError === true) {
                            var registerRedirect = "Register here";
                            var registrationPage = "https://login.pultemortgage.com/registernow";

                            self.errorMessage = "No account was found. " + registerRedirect.link(registrationPage) + " to get started.";
                            return;


                            
                        }
                        else if (loginResponse.data.requiresTwoFactor === true) {

                            return this.$store.dispatch('setTwoFAEmail', this.displayUsername).then(x=> {
                                var twofaUrl = `/twoStepVerification`;
                                this.$router.push({ path: twofaUrl });
                            });

                           
                            
                           // return;
                        }
                        else {
                            console.log("app: login: Invalid credentials!");
                            self.errorMessage = "Invalid credentials provided.";
                            self.inlineErrorMessage = "Invalid email or password. Please try again.";
                            return;
                        }
                    }

                    // authenticated
                    // have login site OIDC connect client sign in to establish the user in this app,
                    // then redirect back to original redirect request
                    self.toggleFooter();

                    self.setWebUser({
                        isLockedout: false,
                        lockOutEnd: null
                    });
                    self.setRememberMeCookie();

                    // redirect to wherever the user needs to go
                    var actualRedirectUri = loginResponse.data.returnUrl;
                    if (self.redirectUri && self.redirectUri !== '') {
                        actualRedirectUri = self.redirectUri;
                    }
                    return self.loginRedirect(actualRedirectUri);
                }).catch(err => {
                    console.log(`app: login: error: login error`);
                    self.errorMessage = err.message;
                }).finally(() => {
                    self.isLoggingIn = false;
                    self.disableInput = false;
                });
        },
        resetPassword(event) {
            var self = this;

            self.isPasswordResetting = true;
            self.disableInput = true;

            var data = {
                Username: this.displayUsername,
                DateOfBirth: new Date(this.dateOfBirth)
            };

            let identityBaseUri = this.$config.identityApiBaseUrl;
            siteApi.resetPassword(identityBaseUri, data)
                .then(response => {
                    // User Story #94133 Removing the display of error text to end user in support of security measures. Always emit regardless of outcome
                    if (response && response.data && response.data.success === false) {
                        self.disableInput = false;
                    }
                    self.$emit('passwordreset');
                }).catch(err => {
                    console.log(`app: login: error: reset password error`);
                    self.errorMessage = err.message;
                    self.disableInput = false;
                }).finally(() => {
                    self.isPasswordResetting = false;
                });
        },
        setCookie(cname, cvalue, exdays) {
            var d = new Date();
            d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
            var expires = "expires=" + d.toUTCString();
            document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
        },
        setRememberMeCookie() {
            var rememberMeCookie = this.checkRememberMeCookie();
            if (!rememberMeCookie && this.shouldRememberMe) {
                this.setCookie('pulteusername', this.username, 365);
                return;
            }
            if (!this.shouldRememberMe) {
                this.setCookie('pulteusername', this.username, -5);
                return;
            }
        },
        validateForm(event) {
            var self = this;
            event.preventDefault();
            this.errorMessage = '';
            self.inlineErrorMessage = '';

            if (!this.doUsernameValidation())
                return;

            if (this.passwordReset) {
                if (!this.doDateOfBirthValidation())
                    return;

                this.resetPassword(event).then(() => self.$emit('passwordreset'));
            }
            else {
                if (!this.doPasswordValidation())
                    return;

                this.login(event);
            }
        }
    }
};
</script>

<style lang="scss">
#loginformsection {
    form.login-form {
        .login-btn {
            border-color: #088354 !important;
            background-color: #088354 !important;
            border-radius: 5px;
            min-height: 44px;
            font-size: 24px;
            color: #ffffff;
            font-family: 'Source Sans Pro', Arial, Helvetica, sans-serif;
            font-weight: 900;
            width: 100%;

            &:focus {
                box-shadow: 0 0 0 .2rem #007bff40;
            }

            &.inverted {
                background-color: #ffffff !important;
                color: #088354;
                border: 2px solid #088354;
                max-width: 100%;

                &:focus {
                box-shadow: 0 0 0 .2rem #007bff40;
                }
            }
        }

        span.required {
            color: red !important;
        }

    }

    .help-container {
        text-align: center;
        padding-bottom: 0.6rem;

        .icn {
            font-size: 20px !important;
        }

        .help-links {
            a {
                font-size: 14px;
                color: #003c5b;
                text-decoration: underline;
            }

            .help-link-seperator {
                font-size: 16px;
                font-family: 'Source Sans Pro';
                color: #202a2f;
                margin-left: 6px;
                margin-right: 6px;
            }

        }

        .facolor {
            color: #075a77;
        }

        .faItalic {
            font-weight: 900;
            font-style: italic;
        }

    }
}



#loginformsection .tooltip[data-popover='iconHover'] {
    background: rgb(247, 244, 244);
    color: #080808;
}

@media (-ms-high-contrast: none),
(-ms-high-contrast: active) {
    .ie {
        transform: translate(-0.1875em, -0.7475em) scale(0.6875) rotate(0deg);
    }
}

@supports not (-ms-high-contrast: none) {

    /* Non-IE styles here */
    .nonie {
        transform: translate(-0.15em, -0.72em) scale(0.6875) rotate(0deg)
    }
}
</style>
<style scoped lang="scss">
.fainputerror {
  margin-right: 8px;

  &.shift {
    margin-right: 30px;
  }
}
.form-control.is-invalid {
  background-image: none !important;
}
.custom-ps{
    padding-left: 1.8rem;
}
</style>